import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Wrench, 
    Plus, 
    Search,
    Filter,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Settings,
    Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EQUIPMENT_TYPES = [
    'Chainsaw',
    'Generator',
    'Pump',
    'Radio',
    'First Aid Kit',
    'Lighting',
    'Rope/Rigging',
    'PPE',
    'Hand Tools',
    'Other'
];

const STATUS_OPTIONS = [
    { value: 'available', label: 'Available', color: 'status-available' },
    { value: 'in_use', label: 'In Use', color: 'status-info' },
    { value: 'maintenance', label: 'Maintenance', color: 'status-warning' },
    { value: 'out_of_service', label: 'Out of Service', color: 'status-unavailable' },
];

export default function EquipmentPage() {
    const { hasUnitAccess } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        equipment_type: '',
        serial_number: '',
        status: 'available',
        location: '',
        last_inspection: '',
        next_inspection: '',
        notes: ''
    });
    
    useEffect(() => {
        fetchEquipment();
    }, []);
    
    const fetchEquipment = async () => {
        try {
            const response = await axios.get(`${API_URL}/equipment`);
            setEquipment(response.data);
        } catch (error) {
            console.error('Failed to fetch equipment:', error);
            toast.error('Failed to load equipment');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingEquipment) {
                await axios.put(`${API_URL}/equipment/${editingEquipment.id}`, formData);
                toast.success('Equipment updated');
            } else {
                await axios.post(`${API_URL}/equipment`, formData);
                toast.success('Equipment added');
            }
            setShowAddDialog(false);
            setEditingEquipment(null);
            resetForm();
            fetchEquipment();
        } catch (error) {
            toast.error('Failed to save equipment');
        }
    };
    
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this equipment?')) return;
        try {
            await axios.delete(`${API_URL}/equipment/${id}`);
            toast.success('Equipment deleted');
            fetchEquipment();
        } catch (error) {
            toast.error('Failed to delete equipment');
        }
    };
    
    const handleEdit = (item) => {
        setFormData({
            name: item.name,
            equipment_type: item.equipment_type,
            serial_number: item.serial_number || '',
            status: item.status,
            location: item.location || '',
            last_inspection: item.last_inspection || '',
            next_inspection: item.next_inspection || '',
            notes: item.notes || ''
        });
        setEditingEquipment(item);
        setShowAddDialog(true);
    };
    
    const resetForm = () => {
        setFormData({
            name: '',
            equipment_type: '',
            serial_number: '',
            status: 'available',
            location: '',
            last_inspection: '',
            next_inspection: '',
            notes: ''
        });
    };
    
    const filteredEquipment = equipment.filter(item => {
        const matchesSearch = 
            item.name?.toLowerCase().includes(search.toLowerCase()) ||
            item.serial_number?.toLowerCase().includes(search.toLowerCase());
        const matchesType = typeFilter === 'all' || item.equipment_type === typeFilter;
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        return matchesSearch && matchesType && matchesStatus;
    });
    
    const stats = {
        total: equipment.length,
        available: equipment.filter(e => e.status === 'available').length,
        maintenance: equipment.filter(e => e.status === 'maintenance').length,
        outOfService: equipment.filter(e => e.status === 'out_of_service').length
    };
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-6">
                                <div className="skeleton h-4 w-24 mb-2 rounded"></div>
                                <div className="skeleton h-8 w-16 rounded"></div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="equipment-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Equipment</h2>
                    <p className="text-muted-foreground">
                        Manage and track unit equipment
                    </p>
                </div>
                {hasUnitAccess() && (
                    <Dialog open={showAddDialog} onOpenChange={(open) => {
                        setShowAddDialog(open);
                        if (!open) {
                            setEditingEquipment(null);
                            resetForm();
                        }
                    }}>
                        <DialogTrigger asChild>
                            <Button className="ses-btn-primary gap-2" data-testid="add-equipment-btn">
                                <Plus className="w-4 h-4" />
                                Add Equipment
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>{editingEquipment ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
                                <DialogDescription>
                                    {editingEquipment ? 'Update equipment details' : 'Add new equipment to the inventory'}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Equipment name"
                                        required
                                        data-testid="equipment-name"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select
                                            value={formData.equipment_type}
                                            onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                                        >
                                            <SelectTrigger data-testid="equipment-type">
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {EQUIPMENT_TYPES.map((type) => (
                                                    <SelectItem key={type} value={type}>
                                                        {type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Status</Label>
                                        <Select
                                            value={formData.status}
                                            onValueChange={(value) => setFormData({ ...formData, status: value })}
                                        >
                                            <SelectTrigger data-testid="equipment-status">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STATUS_OPTIONS.map((status) => (
                                                    <SelectItem key={status.value} value={status.value}>
                                                        {status.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Serial Number</Label>
                                    <Input
                                        value={formData.serial_number}
                                        onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                                        placeholder="Serial number (optional)"
                                        data-testid="equipment-serial"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Location</Label>
                                    <Input
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        placeholder="Storage location"
                                        data-testid="equipment-location"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Last Inspection</Label>
                                        <Input
                                            type="date"
                                            value={formData.last_inspection}
                                            onChange={(e) => setFormData({ ...formData, last_inspection: e.target.value })}
                                            data-testid="equipment-last-inspection"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Next Inspection</Label>
                                        <Input
                                            type="date"
                                            value={formData.next_inspection}
                                            onChange={(e) => setFormData({ ...formData, next_inspection: e.target.value })}
                                            data-testid="equipment-next-inspection"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        placeholder="Additional notes"
                                        data-testid="equipment-notes"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-equipment">
                                        {editingEquipment ? 'Update' : 'Add'} Equipment
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Wrench className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total</p>
                                <p className="text-xl font-bold">{stats.total}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Available</p>
                                <p className="text-xl font-bold">{stats.available}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                                <Settings className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Maintenance</p>
                                <p className="text-xl font-bold">{stats.maintenance}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                                <XCircle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Out of Service</p>
                                <p className="text-xl font-bold">{stats.outOfService}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search equipment..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-equipment"
                            />
                        </div>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-full sm:w-40" data-testid="filter-type">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                {EQUIPMENT_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                        {type}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                {STATUS_OPTIONS.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                        {status.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
            
            {/* Equipment Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredEquipment.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Serial #</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Location</TableHead>
                                        <TableHead>Next Inspection</TableHead>
                                        {hasUnitAccess() && <TableHead className="text-right">Actions</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredEquipment.map((item) => (
                                        <TableRow key={item.id} className="table-row-hover">
                                            <TableCell className="font-medium">{item.name}</TableCell>
                                            <TableCell>{item.equipment_type}</TableCell>
                                            <TableCell className="mono text-sm">{item.serial_number || '-'}</TableCell>
                                            <TableCell>
                                                <Badge className={`status-badge ${STATUS_OPTIONS.find(s => s.value === item.status)?.color}`}>
                                                    {STATUS_OPTIONS.find(s => s.value === item.status)?.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{item.location || '-'}</TableCell>
                                            <TableCell>
                                                {item.next_inspection ? format(new Date(item.next_inspection), 'MMM d, yyyy') : '-'}
                                            </TableCell>
                                            {hasUnitAccess() && (
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(item)}
                                                        data-testid={`edit-equipment-${item.id}`}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-destructive"
                                                        onClick={() => handleDelete(item.id)}
                                                        data-testid={`delete-equipment-${item.id}`}
                                                    >
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <Wrench className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                            <h3 className="font-medium text-lg">No equipment found</h3>
                            <p className="text-muted-foreground mt-1">
                                {search || typeFilter !== 'all' || statusFilter !== 'all'
                                    ? 'Try adjusting your filters'
                                    : 'Add your first equipment item'}
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
